import { useEffect } from "react";

import { MARKER_ENABLED_ENVS, MARKER_MASK_CLASS } from "@constants/marker";

import { useUserProfile } from "@hooks/useUserProfile";

import { getEnv } from "@utils/env";

type MarkerReporter = {
  email: string;
  fullName: string;
};

type MarkerApi = {
  /** Calls made before the widget bundle lands, replayed by it on load. */
  __cs?: unknown[][];
  setReporter?: (reporter: MarkerReporter) => void;
  [method: string]: unknown;
};

declare global {
  interface Window {
    markerConfig?: Record<string, unknown> & { reporter?: MarkerReporter };
    Marker?: MarkerApi;
    /** Set by the snippet so a second copy of it is a no-op. */
    __Marker?: unknown;
  }
}

const SHIM_SRC = "https://edge.marker.io/latest/shim.js";

/**
 * The widget's public API, per Marker.io's install snippet. Each name becomes a
 * queueing stub so callers never have to wait for the bundle.
 */
const MARKER_METHODS = [
  "show",
  "hide",
  "isVisible",
  "capture",
  "cancelCapture",
  "unload",
  "reload",
  "isExtensionInstalled",
  "setReporter",
  "clearReporter",
  "setCustomData",
  "on",
  "off",
] as const;

/**
 * Install the queueing stub and load the shim — Marker.io's own snippet,
 * transcribed. The stub matters: identity resolves from `/me` well after mount,
 * and without it a `setReporter` call landing before the bundle would vanish.
 */
const installWidget = () => {
  if (window.__Marker) return;
  window.__Marker = {};

  const queue: unknown[][] = [];
  const api: MarkerApi = { __cs: queue };
  MARKER_METHODS.forEach((method) => {
    api[method] = (...args: unknown[]) => queue.push([method, ...args]);
  });
  window.Marker = api;

  const script = document.createElement("script");
  script.async = true;
  script.src = SHIM_SRC;
  document.head.appendChild(script);
};

/**
 * Loads the Marker.io feedback widget in allow-listed environments only and
 * tells it who is reporting.
 *
 * Captures leave the browser for a third party, so the gate fails closed: an
 * environment that is missing, unexpected or prod, or a build with no project
 * id, never loads it. Where it does load, page text is masked so screenshots
 * and replays carry no patient details; document previews exclude themselves
 * (`MARKER_EXCLUDE_CLASS`). Masking is a Marker.io plan feature — without it
 * the classes are inert (MDRS-557).
 *
 * Without a reporter, a bug filed from the widget arrives with no idea who sent
 * it unless that person happens to have typed their email into Marker before.
 * External (email-code) users have no MSAL profile, so they were always
 * anonymous — `useUserProfile` now resolves them from `/me` (MDRS-334).
 */
export const MarkerWidget = () => {
  const profile = useUserProfile();
  const email = profile?.email ?? "";
  const fullName = profile?.name || email;
  const project = getEnv("VITE_MARKERIO_PROJECT");
  const enabled = MARKER_ENABLED_ENVS.includes(getEnv("VITE_ENV")) && !!project;

  useEffect(() => {
    if (!enabled) return;

    // Never removed: the widget outlives this component once loaded, and
    // nothing it captures may go unmasked.
    document.body.classList.add(MARKER_MASK_CLASS);

    window.markerConfig = {
      ...window.markerConfig,
      project,
      source: "snippet",
    };

    installWidget();
  }, [enabled, project]);

  useEffect(() => {
    if (!enabled || !email) return;

    const reporter: MarkerReporter = { email, fullName };
    // Read by the widget at init; `setReporter` covers identity that resolves
    // after that. Nothing calls `clearReporter` on the way out because both
    // sign-out paths navigate away (`useAuth`), tearing the widget down with the
    // page.
    window.markerConfig = { ...window.markerConfig, reporter };
    window.Marker?.setReporter?.(reporter);
  }, [enabled, email, fullName]);

  return null;
};
